import styled from "@emotion/styled";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";

const StyledDialogContent = styled(DialogContent)`
	padding: 0 !important;
`;

const StyledJsonEditor = styled.textarea`
	width: 100%;
	min-height: 600px;
	max-height: 80vh;
	padding: 16px;
	margin: 0;
	background-color: #1e1e1e;
	border: none;
	border-radius: 0;
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
	font-size: 13px;
	line-height: 1.6;
	color: #d4d4d4;
	resize: vertical;
	box-sizing: border-box;

	&:focus {
		outline: none;
		background-color: #252526;
	}

	&::selection {
		background-color: rgba(38, 79, 120, 0.99);
	}

	&::placeholder {
		color: #6a737d;
	}
`;

const ErrorMessage = styled.div`
	color: #f48771;
	padding: 8px 16px;
	background-color: #1e1e1e;
	font-size: 14px;
	border-top: 1px solid #444;
`;

interface JsonEditorDialogProps {
	open: boolean;
	title: string;
	value: string;
	onClose: () => void;
	onSave: (value: string) => void;
}

export const JsonEditorDialog: FC<JsonEditorDialogProps> = ({
	open,
	title,
	value,
	onClose,
	onSave,
}) => {
	const [editedValue, setEditedValue] = useState(value);
	const [error, setError] = useState("");

	useEffect(() => {
		if (open) {
			setEditedValue(value);
			setError("");
		}
	}, [open]);

	const handleSave = () => {
		// Try to parse and format the JSON
		try {
			const parsed = JSON.parse(editedValue);
			const formatted = JSON.stringify(parsed, null, 2);
			onSave(formatted);
			onClose();
		} catch (e) {
			setError(
				`Invalid JSON: ${e instanceof Error ? e.message : "Unknown error"}`,
			);
		}
	};

	const handleFormat = () => {
		try {
			const parsed = JSON.parse(editedValue);
			const formatted = JSON.stringify(parsed, null, 2);
			setEditedValue(formatted);
			setError("");
		} catch (e) {
			setError(
				`Cannot format: ${e instanceof Error ? e.message : "Unknown error"}`,
			);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<StyledDialogContent>
				<StyledJsonEditor
					value={editedValue}
					onChange={(e) => {
						setEditedValue(e.target.value);
						setError("");
					}}
					placeholder="Enter JSON..."
					spellCheck={false}
					autoFocus
				/>
				{error && <ErrorMessage>{error}</ErrorMessage>}
			</StyledDialogContent>
			<DialogActions>
				<Button onClick={handleFormat} color="inherit">
					Format JSON
				</Button>
				<Button onClick={onClose} color="inherit">
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" color="primary">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
